import image from "../../Images/TransitGateway.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TransitGateway = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
            const handleScroll = () => {
                const sections = document.querySelectorAll("[data-section]");
                let current = "";
    
                sections.forEach((section) => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.clientHeight;
                    if (window.pageYOffset >= sectionTop - 100) {
                        current = section.getAttribute("data-section");
                    }
                });
    
                setActiveSection(current);
            };
    
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }, []);
    
        const scrollToSection = (sectionId) => {
            const element = document.querySelector(`[data-section="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                    <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>
            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Transit Gateway Stack`}
                    </ReactMarkdown>
                </div>
                <div className="image-container" data-section="architecture">
                    <img className="image" src={image} />
                </div>
                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **Transit Gateway**: Regional network hub acting as a cloud router connecting multiple VPCs, VPN connections, and Direct Connect gateways
- **VPC Attachments**: Connections between Transit Gateway and multiple VPCs, with subnet IDs specified per attachment
- **Route Tables**: Automatic or custom route table associations and propagations for controlling traffic flow between attachments
- **Private Subnets**: Subnets within each VPC where Transit Gateway attachments are created (typically one per AZ)
## Use Cases
- Network topology connecting 10+ VPCs without complex peering mesh
- Centralized network security inspection using firewall appliances in dedicated security VPCs
- Multi-region connectivity when combined with inter-region peering
- Hybrid cloud architectures connecting multiple AWS VPCs to on-premises networks via single VPN/Direct Connect
- Multi-account AWS Organizations with centralized networking
## Cost
- ~36$/month per VPC/VPN attachment

                        `}
                    </ReactMarkdown>
                </div>
                <div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                    <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
#!/usr/bin/env node
import * as cdk from 'aws-cdk-lib/core';
import { TransitGatewayStack } from '../lib/transit_gateway_stack';

const app = new cdk.App();


new TransitGatewayStack(app, 'CorporateTGW', {
  transitGatewayName: 'Corporate-TGW',
  vpcAttachments: [
    {
      vpcId: 'vpc-abc123',  
      subnetIds: ['subnet-111', 'subnet-222', 'subnet-333'], 
      name: 'Production-VPC',
    },
    {
      vpcId: 'vpc-def456',
      subnetIds: ['subnet-444', 'subnet-555'],
      name: 'Development-VPC',
    },
    {
      vpcId: 'vpc-ghi789',  
      subnetIds: ['subnet-666', 'subnet-777', 'subnet-888'],
      name: 'Shared-Services-VPC',
    },
  ],
  env: { 
    account: process.env.CDK_DEFAULT_ACCOUNT, 
    region: process.env.CDK_DEFAULT_REGION 
  },
});
`}
                    </SyntaxHighlighter>
                </div>
                    
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# transit-gateway-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";

interface VpcAttachment {
  vpcId: string;
  subnetIds: string[];
  name: string;
  appliance?: boolean;
}

interface TransitGatewayStackProps extends cdk.StackProps {
  transitGatewayName?: string;
  amazonSideAsn?: number;
  vpcAttachments: VpcAttachment[];
  defaultRouteTableAssociation?: boolean;
  defaultRouteTablePropagation?: boolean;
  dnsSupport?: boolean;
  vpnEcmpSupport?: boolean;
  multicastSupport?: boolean;
}

export class TransitGatewayStack extends cdk.Stack{
  public readonly transitGateway: ec2.CfnTransitGateway;
  public readonly transitGatewayId: string;
  
  constructor(scope: Construct, id: string, props: TransitGatewayStackProps) {
    super(scope, id, props)

    this.transitGateway = new ec2.CfnTransitGateway(this, "TransitGateway", {
      amazonSideAsn: props.amazonSideAsn || 64512,
      description: props.transitGatewayName || "Transit Gateway",
      defaultRouteTableAssociation: props.defaultRouteTableAssociation != false ? "enable" : "disable",
      defaultRouteTablePropagation: props.defaultRouteTablePropagation != false ? "enable" : "disable",
      dnsSupport: props.dnsSupport !== false ? "enable" : "disable",
      vpnEcmpSupport: props.vpnEcmpSupport !== false ? "enable" : "disable",
      multicastSupport: props.multicastSupport !== false ? "enable" : "disable",
      tags: [{
        key: "Name",
        value: props.transitGatewayName || "TransitGateway",
      }]
    })

    this.transitGatewayId = this.transitGateway.ref;

    props.vpcAttachments.forEach((attachment, index) => {
      const gatewayAttachment = new ec2.CfnTransitGatewayAttachment(this, ${"Attachment${index}"}, {
        transitGatewayId: this.transitGatewayId,
        vpcId: attachment.vpcId,
        subnetIds: attachment.subnetIds,
        options: {
          ApplianceModeSupport: attachment.appliance ? "enable" : "disable",

        },
        tags: [{
          key: "Name",
          value: attachment.name
        }]
      })
      gatewayAttachment.addDependency(this.transitGateway);
    })

    props.vpcAttachments.forEach((attachment, index) => {
      const vpc = ec2.Vpc.fromLookup(this, ${"Vpc${index}"}, {
        vpcId: attachment.vpcId,
      })
      vpc.privateSubnets.forEach((subnet, subnetIndex) => {
        const otherVpcCidrs = props.vpcAttachments.filter((_, i) => i !== index)
        .map(att => {
          const otherVpc = ec2.Vpc.fromLookup(this, ${"OtherVpc${index}-${subnetIndex}"}, {
            vpcId: att.vpcId,
          })
          return otherVpc.vpcCidrBlock;
        })

        otherVpcCidrs.forEach((cidr, cidrIndex) => {
          new ec2.CfnRoute(this, ${"Route${index}-${subnetIndex}-${cidrIndex}"}, {
            routeTableId: subnet.routeTable.routeTableId,
            destinationCidrBlock: cidr,
            transitGatewayId: this.transitGatewayId,
          })
        })
      })
    })

    new cdk.CfnOutput(this, "TransitGatewayId", {
      value: this.transitGatewayId,
      description: "Transit Gateway ID",
      exportName: ${"${id}-TgwId"},
    })

    props.vpcAttachments.forEach((attachment, index) => {
      new cdk.CfnOutput(this, ${"Attachment${index}"}, {
        value: attachment.name,
        description: ${"VPC attachment: ${attachment.name}"},
      });
    })

    new cdk.CfnOutput(this, 'ConnectedVpcs', {
      value: props.vpcAttachments.length.toString(),
      description: 'Number of VPCs connected',
    });
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";

interface VpcAttachment {
  vpcId: string;
  subnetIds: string[];
  name: string;
  appliance?: boolean;
}

interface TransitGatewayStackProps extends cdk.StackProps {
  transitGatewayName?: string;
  amazonSideAsn?: number;
  vpcAttachments: VpcAttachment[];
  defaultRouteTableAssociation?: boolean;
  defaultRouteTablePropagation?: boolean;
  dnsSupport?: boolean;
  vpnEcmpSupport?: boolean;
  multicastSupport?: boolean;
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-ec2** - EC2 and VPC constructs for creating Transit Gateway and VPC networking resources
## VpcAttachment Interface
- **vpcId** - The ID of the VPC to attach to the Transit Gateway
- **subnetIds** - Array of subnet IDs within the VPC to use for the attachment (typically one subnet per AZ)
- **name** - Descriptive name for the VPC attachment for identification
- **appliance** - Optional flag to enable appliance mode for inspecting traffic through network appliances (e.g., firewalls)
## TransitGatewayStackProps
- **transitGatewayName** -  custom name for the Transit Gateway resource
- **amazonSideAsn** -  Autonomous System Number for BGP routing (default is 64512, must be in valid range)
- **vpcAttachments** -  array of VPC attachments defining which VPCs connect to the Transit Gateway
- **defaultRouteTableAssociation** -  flag to automatically associate attachments with the default route table
- **defaultRouteTablePropagation** -  flag to automatically propagate routes from attachments to the default route table
- **dnsSupport** -  flag to enable DNS resolution between attached VPCs
- **vpnEcmpSupport** -  flag to enable Equal-Cost Multi-Path routing for VPN connections (load balancing across multiple tunnels)
- **multicastSupport** -  flag to enable multicast traffic routing between VPCs (for specialized applications)

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & Public Attributes`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class TransitGatewayStack extends cdk.Stack{
  public readonly transitGateway: ec2.CfnTransitGateway;
  public readonly transitGatewayId: string;
  
  constructor(scope: Construct, id: string, props: TransitGatewayStackProps) {
    super(scope, id, props)
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## Public Attributes
- **transitGateway** - Exposes the Transit Gateway resource as a public readonly property so other stacks can reference it
- **transitGatewayId** - Exposes the Transit Gateway ID as a public readonly property for cross-stack references and route table configurations
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Transit Gateway Setup`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    this.transitGateway = new ec2.CfnTransitGateway(this, "TransitGateway", {
      amazonSideAsn: props.amazonSideAsn || 64512,
      description: props.transitGatewayName || "Transit Gateway",
      defaultRouteTableAssociation: props.defaultRouteTableAssociation != false ? "enable" : "disable",
      defaultRouteTablePropagation: props.defaultRouteTablePropagation != false ? "enable" : "disable",
      dnsSupport: props.dnsSupport !== false ? "enable" : "disable",
      vpnEcmpSupport: props.vpnEcmpSupport !== false ? "enable" : "disable",
      multicastSupport: props.multicastSupport !== false ? "enable" : "disable",
      tags: [{
        key: "Name",
        value: props.transitGatewayName || "TransitGateway",
      }]
    })

    this.transitGatewayId = this.transitGateway.ref;
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## TG Setup
- Creates the Transit Gateway using CloudFormation (Cfn) construct and assigns it to the public transitGateway property
- **amazonSideAsn** - Sets the ASN for BGP routing, defaults to 64512 (private ASN range) if not specified
- **description** - Uses custom name from props or defaults to "Transit Gateway" for documentation
- **defaultRouteTableAssociation** - Enabled by default (unless explicitly set to false), automatically associates new attachments with the default route table
- **defaultRouteTablePropagation** - Enabled by default (unless explicitly set to false), automatically propagates routes from attachments to the default route table
- **dnsSupport** - Enabled by default, allows DNS resolution between attached VPCs
- **vpnEcmpSupport** - Enabled by default, enables Equal-Cost Multi-Path routing for load balancing VPN traffic across multiple tunnels
- **multicastSupport** - Enabled by default, allows multicast traffic routing (disabled by default in some regions due to limited support)
`}
                    </ReactMarkdown>
                    </div>

                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Gateway VPC Attachments`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
props.vpcAttachments.forEach((attachment, index) => {
      const gatewayAttachment = new ec2.CfnTransitGatewayAttachment(this, ${"Attachment${index}"}, {
        transitGatewayId: this.transitGatewayId,
        vpcId: attachment.vpcId,
        subnetIds: attachment.subnetIds,
        options: {
          ApplianceModeSupport: attachment.appliance ? "enable" : "disable",

        },
        tags: [{
          key: "Name",
          value: attachment.name
        }]
      })
      gatewayAttachment.addDependency(this.transitGateway);
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Attachment Loop
- Iterates through each VPC attachment defined in the vpcAttachments array from props
- Creates a separate Transit Gateway attachment resource for each VPC
## Configurations
- **transitGatewayId** - Links the attachment to the Transit Gateway created earlier
- **vpcId** - Specifies which VPC to attach to the Transit Gateway
- **subnetIds** - Defines which subnets in the VPC to use for the attachment (typically one per AZ for high availability)
- **addDependency(transitGateway)** - Ensures the Transit Gateway is fully created before attempting to create attachments
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Transit Gateway Routing`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
props.vpcAttachments.forEach((attachment, index) => {
      const vpc = ec2.Vpc.fromLookup(this, ${"Vpc${index}"}, {
        vpcId: attachment.vpcId,
      })
      vpc.privateSubnets.forEach((subnet, subnetIndex) => {
        const otherVpcCidrs = props.vpcAttachments.filter((_, i) => i !== index)
        .map(att => {
          const otherVpc = ec2.Vpc.fromLookup(this, ${"OtherVpc${index}-${subnetIndex}"}, {
            vpcId: att.vpcId,
          })
          return otherVpc.vpcCidrBlock;
        })

        otherVpcCidrs.forEach((cidr, cidrIndex) => {
          new ec2.CfnRoute(this, ${"Route${index}-${subnetIndex}-${cidrIndex}"}, {
            routeTableId: subnet.routeTable.routeTableId,
            destinationCidrBlock: cidr,
            transitGatewayId: this.transitGatewayId,
          })
        })
      })
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## TG Routing
- Iterates through each VPC attachment to configure routing
- **Vpc.fromLookup** - Imports existing VPC details by VPC ID to access subnet and CIDR information
- Loops through all private subnets in each attached VPC
- For each subnet, creates routes to all other VPCs attached to the Transit Gateway
- **filter((_, i) => i !== index)** - Excludes the current VPC to avoid creating routes to itself
- Looks up each other attached VPC and extracts its CIDR block (IP address range)
- Creates an array of all destination CIDR blocks that traffic should be routed to via Transit Gateway
- Creates a CfnRoute for each combination of subnet and destination VPC CIDR
- **routeTableId** - Targets the specific route table for each private subnet
- **destinationCidrBlock** - Sets the destination as another VPC's CIDR range
- **transitGatewayId** - Routes matching traffic through the Transit Gateway


`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    new cdk.CfnOutput(this, "TransitGatewayId", {
      value: this.transitGatewayId,
      description: "Transit Gateway ID",
      exportName: ${"${id}"}-TgwId,
    })

    props.vpcAttachments.forEach((attachment, index) => {
      new cdk.CfnOutput(this, ${"Attachment${index}"}, {
        value: attachment.name,
        description: ${"VPC attachment: ${attachment.name}"},
      });
    })

    new cdk.CfnOutput(this, 'ConnectedVpcs', {
      value: props.vpcAttachments.length.toString(),
      description: 'Number of VPCs connected',
    });

  
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )

}
export default TransitGateway;